import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { isNumber } from "util";
import BreadCrumb from "../components/breadCrumb";
import CheckBox from "../components/common/CheckBox";
import ComboBox from "../components/common/ComboBox";
import ComboBoxData from "../components/common/ComboBoxData";
import ComboBoxDataWithColors from "../components/common/ComboBoxDataWithColors";
import DataGrid from "../components/common/DataGrid";
import { Picture } from "../components/common/Picture";
import { SelectBox } from "../components/common/SelectBox";
import Spinner from "../components/common/Spinner";
import { TextBox } from "../components/common/TextBox";
import PageContent from "../components/pageContent";
import ProductBreadCrumb from "../components/products/ProductBreadCrumb";
import ToningPrinterOptions from "../components/toning/ToningPrinterOptions";
import TopBarWithBreadCrumb from "../components/TopBarWithBreadCrumb";
import { checkCrud } from "../helpers/userRolesHelper";
import { productGroupSelectionAction } from "../redux/actions/products/productActions";
import { apiGetAllColorsForSampleCardAndProduct } from "../services/ColorsService";
import { apiGetDiscounts } from "../services/DiscountsService";
import { apiGetAllPackagesForProductAndBase } from "../services/PackagesService";
import {
  apiGetAllProducts,
  apiGetProductAlternatives
} from "../services/ProductsService";
import {
  apiGetPossibleBasisFromReceipt,
  apiGetReceipt,
} from "../services/ReceiptsService";
import { apiGetSampleCardsForProduct } from "../services/SampleCardsService";
import { apiRecalculateToningData } from "../services/ToningService";
import { useTranslation } from "react-i18next";
import ComboBoxDataEditable from "../components/common/ComboBoxDataEditable";
import { useLocation } from "react-router-dom";
import { useHistory } from "react-router-dom";
import i18n from "../i18n";

let config = require("../configuration.json");

require("./TonovaniPage.css");


interface Props {
  contextLevel: string;
  toningEnabled: boolean;
}

const entityName = 'toning';
const translationPrefix = 'Toning';
const translationModel = { prefix: translationPrefix };

const TonovaniPage = (props: Props) => {

  const { t } = useTranslation(undefined, { keyPrefix: translationPrefix });
  const { t: tFields } = useTranslation(undefined, { keyPrefix: "Toning.Fields" });

  const pigmentsGridColumnNames = {
    PigmentName: tFields('PigmentName'),
    HardenerName: tFields('HardenerName'),
    AmountInUnit: tFields('AmountInUnit'),
    Price: tFields('Price'),
    YFormat: tFields('YFormat')
  };

  let initSampleCards: any[] = [];
  let initColors: any[] = [];
  let initBasis: any[] = [];
  let initDiscounts: any[] = [];
  let initProductAlternatives: any[] = [];
  let initSelectedDiscount: any = null;


  const [loading, setLoading] = useState(true);
  const [loadingProducts, setLoadingProducts] = useState(true);
  const [sampleCards, setSampleCards] = useState(initSampleCards);
  const [products, setProducts] = useState<any[]>([]);
  const [alternativesVisible, setAlternativesVisible] = useState(false);
  const [productAlternatives, setProductAlternatives] = useState(initProductAlternatives);
  const [colors, setColors] = useState(initColors);
  const [receiptForThisColorNotFound, setReceiptForThisColorNotFound] = useState(false);
  const [basis, setBasis] = useState(initBasis);
  const [packages, setPackages] = useState([]);

  const [discounts, setDiscounts] = useState(initDiscounts);
  const [selectedDiscount, setSelectedDiscount] = useState(initSelectedDiscount);
  const [numberOfPackages, setNumberOfPackages] = useState(1);
  const [invalidProduct, setInvalidProduct] = useState(false);

  const customWeightInitValue: any = 0;

  const [customWeight, setCustomWeight] = useState({
    enabled: false,
    value: customWeightInitValue,
  });

  const userInfo = useSelector((state: any) => state.userInfo);

  const unitData = [
    { name: t('Units.Kilograms'), id: 10, code: "Kg" },
    { name: t('Units.Grams'), id: 11, code: "G" },
    { name: t('Units.Liters'), id: 21, code: "L" },
    { name: t('Units.Milliliters'), id: 22, code: "Ml" },
    { name: t('Units.Ounces'), id: 30, code: "Oz" },
  ];



  let defaultUnitId = '11';
  let defaultYFormat = false;

  if (!!userInfo.RememberFieldsValues && localStorage.getItem("selectedUnitId") != null) {
    defaultUnitId = localStorage.getItem("selectedUnitId") ?? defaultUnitId;
  }

  if (!!userInfo.RememberFieldsValues && localStorage.getItem("selectedYFormat") != null) {
    defaultYFormat = localStorage.getItem("selectedYFormat") == 'true';
  }

  const defaultUnitCode = unitData.find(a => a.id.toLocaleString() == defaultUnitId)?.code ?? "G";

  let initSelectedValues: any = {
    unitCode: defaultUnitCode,
    weightWithHardernerIncluded: false,
    weightWithPigmentsIncluded: false,
    selectedPigmentsRatio: 0
  };

  const [selectedValues, setSelectedValue] = useState(initSelectedValues);

  const initToningData: any = {};
  const [toningData, setToningData] = useState(initToningData);
  const productInfo = useSelector((state: any) => state.productInfo);
  const dispatch = useDispatch();

  const [canCreate, setCanCreate] = useState(false);
  const [canRead, setCanRead] = useState(false);
  const [canUpdate, setCanUpdate] = useState(false);
  const [canDelete, setCanDelete] = useState(false);

  const [fullScreen, setFullScreen] = useState(false);



  const [yFormatIsActive, setYFormatIsActive] = useState(defaultYFormat);

  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const onlySpecialReceipts = params.get('specialreceipts') ? true : false;
  const onlyCustomReceipts = params.get('customreceipts') ? true : false;
  const receiptSellerId = onlySpecialReceipts ? (userInfo?.SellerId ?? null) : null;
  const history = useHistory();

  const setRoles = () => {

    let canToning = checkCrud(entityName, 'read', userInfo);

    if (userInfo.Anonymous) {

      if (location.search?.length > 0) {
        history.push(`/tonovani${location.search}`);
      } else {
        history.push("/");
      }
    } else if (!canToning) {
      history.push("/noaccess");
    }
    else {
      setCanCreate(checkCrud(entityName, 'create', userInfo));
      setCanRead(canToning);
      setCanUpdate(checkCrud(entityName, 'update', userInfo));
      setCanDelete(checkCrud(entityName, 'delete', userInfo));
    }
  }

  useEffect(() => {
    setRoles();
  }, [userInfo.UserRoles])



  let loadedDiscounts: any;

  useEffect(() => {
    loadData();
  }, [productInfo]);

  useEffect(() => {

    apiGetAllProducts(onlySpecialReceipts).then(
      (responseData) => {
        if (responseData && responseData.data) {
          let arr = responseData.data.sort((a, b) => a.name.localeCompare(b.name)).map((element) => {
            element.value = element.id;
            element.label = element.name;
            return element;
          });
          setProducts(arr);
        }
      }
    ).finally(() => {
      setLoadingProducts(false);
    });


  }, []);

  const ensureDiscounts = async () => {

    if (!discounts || discounts.length <= 0) {
      var loadedDiscountsResponse = await apiGetDiscounts();

      if (loadedDiscountsResponse.status == 200) {

        setDiscounts(loadedDiscountsResponse.data);

        if (loadedDiscountsResponse.data.length > 0) {
          setSelectedDiscount(loadedDiscountsResponse.data[0]);
        }

        return loadedDiscountsResponse.data;
      }
    }

    return null;
  };

  const loadData = async () => {

    loadedDiscounts = await ensureDiscounts();

    if (productInfo && productInfo.productId) {
      apiGetSampleCardsForProduct(productInfo.productId, onlySpecialReceipts, onlyCustomReceipts).then(
        (responseData) => {
          if (responseData && responseData.data) {

            var newSampleCards: any[] = [];
            responseData.data.forEach((item) => {
              newSampleCards.push({ name: item.name, id: item.id });
            });

            if (newSampleCards.length > 0) {
              setInvalidProduct(false);

              if (!!userInfo.RememberFieldsValues && localStorage.getItem("selectedSampleCardId") != null) {
                try {
                  var storedSampleCardIdString = localStorage.getItem("selectedSampleCardId");
                  var storedSampleCardId = parseFloat(storedSampleCardIdString!);

                  var preselectedSapmpleCard = newSampleCards.filter(a => a.id == storedSampleCardId);
                  if (preselectedSapmpleCard && preselectedSapmpleCard.length > 0) {
                    processDataBasedOnSampleCard(preselectedSapmpleCard[0]);
                  }
                  else {
                    processDataBasedOnSampleCard(newSampleCards[0]);
                  }
                }
                catch {
                  processDataBasedOnSampleCard(newSampleCards[0]);
                }
              }
              else {
                processDataBasedOnSampleCard(newSampleCards[0]);
              }
            }
            else {
              setLoading(false);
              setSelectedValue({
                ...selectedValues,
                sampleCard: null,
                color: null,
                base: null,
                package: null,
                receipt: null,
                hardener: null
              });
              setColors(initColors);
              setBasis(initBasis);
              setPackages([]);
              setToningData(initToningData);
              setInvalidProduct(true);
            }

            setSampleCards(newSampleCards);
          }
          else {
            setSelectedValue({
              ...selectedValues,
            });
          }
        }
      ).catch(() => {
        setLoading(false);
      });
    }
    else {
      setLoading(false);
    }
  };

  const handleCustomWeightChange = async (value: number) => {
    setLoading(true);

    var newState = { ...selectedValues };
    var newCustomWeight = { ...customWeight };
    newCustomWeight.value = value || 0;
    await recalculateToningData(newState, null, newCustomWeight);

    setLoading(false);
  };

  const handleSampleCardChange = (value: number) => {
    setLoading(true);
    var sampleCard = sampleCards.filter(sampleCard => sampleCard.id == value);

    if (sampleCard && sampleCard.length > 0) {

      // try to save value
      localStorage.setItem("selectedSampleCardId", value.toString());

      processDataBasedOnSampleCard(sampleCard[0]);
    }
    else {
      localStorage.removeItem("selectedSampleCardId");
    }

  };

  const handleProductAlternativeChange = (value: number) => {
    handleProductSelectChange(value, productAlternatives);
  };

  const handleColorChange = (colorId: number) => {
    setLoading(true);

    // try to save value
    localStorage.setItem("selectedColorId", colorId.toString());

    processColorChange(selectedValues.sampleCard, colorId);
  };

  const processColorChange = (sampleCard: any, colorId: number, colorsParam?: any) => {

    var providedColors = colorsParam || colors;
    var color = providedColors.filter((a) => a.id == colorId);

    let newSelectedValues = { ...selectedValues };
    newSelectedValues.color = color[0];
    newSelectedValues.sampleCard = sampleCard;

    apiGetPossibleBasisFromReceipt(colorId, productInfo.productId).then(
      (response) => {
        if (response.data && response.data.length > 0) {
          setReceiptForThisColorNotFound(false);
          let basisFromResponse = response.data;
          setBasis(basisFromResponse);
          newSelectedValues.base = response.data[0];
          processDataBasedOnBase(
            basisFromResponse[0].id,
            colorId,
            basisFromResponse,
            newSelectedValues
          );
        }
        else {
          setLoading(false);
          setReceiptForThisColorNotFound(true);
          setPackages([]);
          setToningData(initToningData);
          setSelectedValue(newSelectedValues);
        }
      }
    ).catch(() => {
      setReceiptForThisColorNotFound(true);
      setPackages([]);
      setToningData(initToningData);
      setLoading(false);
      setSelectedValue(newSelectedValues);
    });
  };

  const handleBaseChange = (value: number) => {

    setLoading(true);
    processDataBasedOnBase(
      value,
      selectedValues.color.id,
      basis,
      selectedValues
    );
  };

  const handlePackageChange = async (packageId: number) => {

    setLoading(true);
    processDataBasedOnPackage(packageId, packages);
  };

  /*
  const handleHardenerChange = async (hardenerId: number) => {

    setLoading(true);
    let currentHardener = selectedValues.package.hardeners.filter((a: any) => a.id == hardenerId)[0];

    if (currentHardener) {
      let newSelectedValues = {
        ...selectedValues
      };

      newSelectedValues.hardener = currentHardener;

      await recalculateToningData(newSelectedValues);
      setSelectedValue(newSelectedValues);
    }
    else {
      setLoading(false);
    }
  }
*/

  const processDataBasedOnSampleCard = async (sampleCard: any) => {

    await apiGetAllColorsForSampleCardAndProduct(sampleCard.id, productInfo.productId, onlySpecialReceipts, onlyCustomReceipts).then((responseData) => {
      var newColors: any[] = [];
      responseData.data.forEach((item) => {
        newColors.push({
          name: item.name,
          id: item.id,
          colorName: item.colorName,
          isPicture: item.isPicture,
          pictureName: item.pictureName,
          rgbColor: item.rgbColor,
        });
      });

      if (newColors.length > 0) {

        if (!!userInfo.RememberFieldsValues && localStorage.getItem("selectedColorId") != null) {
          try {
            var storedColorIdString = localStorage.getItem("selectedColorId");
            var storedColorId = parseFloat(storedColorIdString!);

            var preselectedColor = newColors.filter(a => a.id == storedColorId);
            if (preselectedColor && preselectedColor.length > 0) {
              processColorChange(sampleCard, preselectedColor[0].id, newColors);
            }
            else {
              processColorChange(sampleCard, newColors[0].id, newColors);
            }
          }
          catch {
            processColorChange(sampleCard, newColors[0].id, newColors);
          }
        }
        else {
          processColorChange(sampleCard, newColors[0].id, newColors);
        }
      }
      else {
        setReceiptForThisColorNotFound(true);
        setBasis(initBasis);
        setPackages([]);
        setToningData(initToningData);
        setSelectedValue({
          ...selectedValues,
          sampleCard: sampleCard,
          color: null,
          base: null,
          package: null,
          receipt: null,
          hardener: null
        });

        setLoading(false);
      }

      setColors(newColors);
    }).catch(() => {
      setReceiptForThisColorNotFound(true);
      setBasis(initBasis);
      setPackages([]);
      setToningData(initToningData);
      setSelectedValue({
        ...selectedValues,
        sampleCard: sampleCard,
        color: null,
        base: null,
        package: null,
        receipt: null,
        hardener: null
      }); setLoading(false);
    });


  };

  const processDataBasedOnBase = async (
    baseId: number,
    colorId: number,
    basis: any,
    selectedValuesState: any
  ) => {
    var base = basis.filter((a) => a.id == baseId)[0];

    var currentPackages: any[] = [...packages];

    if (productInfo.productId) {
      await apiGetAllPackagesForProductAndBase(
        productInfo.productId,
        baseId
      ).then((response) => {
        currentPackages = [...response.data];
        setPackages(response.data);
        selectedValuesState.base = base;

        // try to remember package
        if (response.data.length > 0) {

          if (selectedValuesState.package) {
            var selectedPackage = response.data.filter(a => a.id == selectedValuesState.package.id);

            if (selectedPackage && selectedPackage.length > 0) {
              selectedValuesState.package = selectedPackage[0];
            }
            else {
              selectedValuesState.package = response.data[0];
            }
          }
          else {

            // try to load it from localstorage
            let packageName = localStorage.getItem('packageName');

            if (packageName && packageName.length > 0) {
              var selectedPackage = response.data.filter(a => a.friendlyName == packageName);

              if (selectedPackage && selectedPackage.length > 0) {
                selectedValuesState.package = selectedPackage[0];
              }
              else {
                selectedValuesState.package = response.data[0];
              }
            }
            else {
              selectedValuesState.package = response.data[0];
            }
          }
        }
        else {
          selectedValuesState.package = null;
        }
      })
        .catch(() => setLoading(false));

      await apiGetReceipt(colorId, productInfo.productId, baseId, null, receiptSellerId).then(
        (response) => {
          selectedValuesState.receipt = response.data ?? null;

          if (!selectedValuesState.receipt) {
            setReceiptForThisColorNotFound(true);
            setToningData(initToningData);
            setSelectedValue(selectedValuesState);
            setLoading(false);
            return;
          }

          selectedValuesState.selectedPigmentsRatio = selectedValuesState.receipt.pigmentsRatio;
          if (selectedValuesState.package) {
            processDataBasedOnPackage(
              selectedValuesState.package.id,
              currentPackages,
              selectedValuesState
            );
          } else {
            setSelectedValue(selectedValuesState);
            setLoading(false);
          }
        }
      ).catch(() => {
        setReceiptForThisColorNotFound(true);
        setToningData(initToningData);
        setSelectedValue(selectedValuesState);
        setLoading(false);
      });
    }
  };

  const processDataBasedOnPackage = async (
    packageId: number,
    packages: any,
    selectedValuesState?: any
  ) => {
    let currentPackage = packages.filter((a: any) => a.id == packageId)[0];

    if (currentPackage) {

      let packageName = currentPackage.friendlyName;

      localStorage.setItem('packageName', packageName);

      let currentHardener = currentPackage.hardeners && currentPackage.hardeners.length > 0 ? currentPackage.hardeners[0] : null;
      let newSelectedValues = selectedValuesState
        ? {
          ...selectedValuesState,
          hardener: currentHardener,
          package: currentPackage,
        }
        : { ...selectedValues, hardener: currentHardener, package: currentPackage };

      await recalculateToningData(newSelectedValues);
      setSelectedValue(newSelectedValues);
    }
    else {
      setSelectedValue(selectedValuesState);
      setLoading(false);
    }
  };

  const handleDiscountChange = async (discountId: number) => {

    setLoading(true);
    let currentDiscount = discounts.filter((a: any) => a.id == discountId)[0];

    setSelectedDiscount(currentDiscount);
    await recalculateToningData(selectedValues, null, null, currentDiscount);
  };

  const handleUnitChange = async (unitId: number) => {
    setLoading(true);

    var newState = { ...selectedValues };
    newState.unitCode = unitData.filter((a) => a.id == unitId)[0].code;

    const nextYFormat =
      (newState.unitCode === "Oz" || newState.unitCode === "Ml")
        ? yFormatIsActive               // nech jak je (user volí checkboxem)
        : false;                        // jinak vždy vypnout

    if (nextYFormat === false) {
      setYFormatIsActive(false);
      if (userInfo.RememberFieldsValues) {
        localStorage.setItem("selectedYFormat", "false");
      }
    }

    if (!!userInfo.RememberFieldsValues) {
      localStorage.setItem("selectedUnitId", unitId.toString());
    }

    await recalculateToningData(newState, null, null, null, nextYFormat);
    setSelectedValue(newState);
    setLoading(false);
  };

  const recalculateToningData = async (
    selectedValuesState,
    numberOfPackagesParam?,
    customWeightParameter?,
    newDiscount?,
    yFormatOverride?: boolean
  ) => {
    setLoading(true);

    if (
      selectedValuesState &&
      selectedValuesState.receipt &&
      //selectedValues.base &&
      selectedValuesState.package
    ) {

      // check discount
      let currentDiscount = newDiscount || selectedDiscount;

      if (!currentDiscount) {
        if (loadedDiscounts && loadedDiscounts.length > 0) {
          currentDiscount = loadedDiscounts[0];
        }
      }

      let customWeightValueState = customWeightParameter || customWeight;

      let customWeightValue: any = customWeightValueState.enabled && customWeightValueState.value ?
        parseFloat(customWeightValueState.value.toLocaleString() || '0') : 0;

      if (customWeightValueState.enabled && !customWeightValueState.value) {
        customWeightValue = null;
      }

      const yFormatToSend =
        typeof yFormatOverride === "boolean" ? yFormatOverride : yFormatIsActive;

      await apiRecalculateToningData({
        NumberOfPackages: numberOfPackagesParam || numberOfPackages,
        Receipt: selectedValuesState.receipt,
        PigmentsRatio: selectedValuesState.selectedPigmentsRatio,
        Base: selectedValuesState.base,
        Color: selectedValuesState.color,
        SampleCard: selectedValuesState.sampleCard,
        Package: selectedValuesState.package,
        Hardeners: selectedValuesState.package.hardeners,
        WeightWithHardernerIncluded: selectedValuesState.weightWithHardernerIncluded,
        WeightWithPigmentsIncluded: selectedValuesState.weightWithPigmentsIncluded,
        Discount: currentDiscount || null,
        Product: { id: productInfo.productId, name: productInfo.productName, path: productInfo.productPath },
        CustomWeightValue: customWeightValue,
        VolumeUnitCode: selectedValuesState.unitCode,
        yFormatIsActive: yFormatToSend // remember yformat for printing
      }).then(async (response: any) => {

        setLoading(false);
        setToningData(response.data);

        await apiGetProductAlternatives(selectedValuesState.base.name, selectedValuesState.color.id, productInfo.productId).then((responseData: any) => {

          setProductAlternatives(responseData.data.sort((a, b) => a.name.localeCompare(b.name)));
        });
      }).catch((ex) => {

        setLoading(false);

        if (ex.response?.status == 401 || ex.response?.status == 400) {
          alert('Internal error');
          window.location.reload();
        }
      });


    }
  };

  const printCurrentToning = (format: any) => { };

  const primaryDiscount =
    selectedValues.discount && selectedValues.discount.length > 0
      ? selectedValues.discount.filter((a) => a.isPrimary)
      : null;

  const handleProductSelectChange = (value: number, alternativesForProduct?: any) => {

    setReceiptForThisColorNotFound(false);
    setInvalidProduct(false);
    setColors(initColors);
    setBasis(initBasis);
    setPackages([]);
    setToningData(initToningData);
    setSelectedValue({
      ...selectedValues,
      sampleCard: null,
      color: null,
      base: null,
      package: null,
      receipt: null,
      hardener: null
    });

    var productsList = alternativesForProduct || products;

    let selectedProduct = productsList.find((p: any) => p.id == value);
    if (selectedProduct) {
      let payLoad = JSON.stringify({
        productId: selectedProduct.id,
        productGroupPath: selectedProduct.path,
        productPath: selectedProduct.path,
        productName: selectedProduct.name,
        productPrefersLitres: selectedProduct.productPrefersLitres
      });

      localStorage.setItem(`${userInfo.Username}_productInfo`, payLoad);
      dispatch(productGroupSelectionAction(payLoad));
    }
  };

  let specialReceiptsMode = window.location.href.toLocaleLowerCase().indexOf('specialreceipts=true') >= 0;
  let customReceiptsMode = window.location.href.toLocaleLowerCase().indexOf('customreceipts=true') >= 0;

  let defaultPackageId = null;

  if (selectedValues && selectedValues.package) {
    defaultPackageId = selectedValues.package.id;
  }
  else {
    let packageName = localStorage.getItem('packageName');

    if (packageName && packageName.length > 0 && packages && packages.length > 0) {
      let foundPackage: any = packages.filter((a: any) => a.friendlyName == packageName);

      if (foundPackage && foundPackage.length > 0) {
        defaultPackageId = foundPackage[0].id;
      }

    }
  }

  return (
    <>
      <TopBarWithBreadCrumb
        contextLevel={props.contextLevel}
        pageLevel={t('Label')}
      />
      <PageContent>
        <>
          {(loading || loadingProducts) && <Spinner loadingText="Loading" />}


          {!productInfo || !productInfo.productId && (<>
            <div className="row">
              <div className="col-2">
                {!fullScreen &&
                  <div className="enterFullScreenButton" onClick={() => {

                    let topNavigationElement = document.getElementById('topNavigationElement');

                    if (topNavigationElement) {
                      topNavigationElement['style']['display'] = 'none';
                    }

                    setFullScreen(true);
                  }}>
                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="currentColor" className="bi bi-arrows-fullscreen" viewBox="0 0 16 16">
                      <path fill-rule="evenodd" d="M5.828 10.172a.5.5 0 0 0-.707 0l-4.096 4.096V11.5a.5.5 0 0 0-1 0v3.975a.5.5 0 0 0 .5.5H4.5a.5.5 0 0 0 0-1H1.732l4.096-4.096a.5.5 0 0 0 0-.707m4.344 0a.5.5 0 0 1 .707 0l4.096 4.096V11.5a.5.5 0 1 1 1 0v3.975a.5.5 0 0 1-.5.5H11.5a.5.5 0 0 1 0-1h2.768l-4.096-4.096a.5.5 0 0 1 0-.707m0-4.344a.5.5 0 0 0 .707 0l4.096-4.096V4.5a.5.5 0 1 0 1 0V.525a.5.5 0 0 0-.5-.5H11.5a.5.5 0 0 0 0 1h2.768l-4.096 4.096a.5.5 0 0 0 0 .707m-4.344 0a.5.5 0 0 1-.707 0L1.025 1.732V4.5a.5.5 0 0 1-1 0V.525a.5.5 0 0 1 .5-.5H4.5a.5.5 0 0 1 0 1H1.732l4.096 4.096a.5.5 0 0 1 0 .707" />
                    </svg>
                  </div>
                }

                {fullScreen &&
                  <div className="exitFullScreenButton" onClick={() => {

                    let topNavigationElement = document.getElementById('topNavigationElement');

                    if (topNavigationElement) {
                      topNavigationElement['style']['display'] = 'block';
                    }

                    setFullScreen(false);
                  }}>
                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="currentColor" className="bi bi-fullscreen" viewBox="0 0 16 16">
                      <path d="M1.5 1a.5.5 0 0 0-.5.5v4a.5.5 0 0 1-1 0v-4A1.5 1.5 0 0 1 1.5 0h4a.5.5 0 0 1 0 1zM10 .5a.5.5 0 0 1 .5-.5h4A1.5 1.5 0 0 1 16 1.5v4a.5.5 0 0 1-1 0v-4a.5.5 0 0 0-.5-.5h-4a.5.5 0 0 1-.5-.5M.5 10a.5.5 0 0 1 .5.5v4a.5.5 0 0 0 .5.5h4a.5.5 0 0 1 0 1h-4A1.5 1.5 0 0 1 0 14.5v-4a.5.5 0 0 1 .5-.5m15 0a.5.5 0 0 1 .5.5v4a1.5 1.5 0 0 1-1.5 1.5h-4a.5.5 0 0 1 0-1h4a.5.5 0 0 0 .5-.5v-4a.5.5 0 0 1 .5-.5" />
                    </svg>
                  </div>
                }
              </div>
              <div className="col-10">
                <ProductBreadCrumb hidePath={true} selectionOfProductIsRequired={true} /> </div>
            </div>
            <div className="row">
              <div className="col-12">
                <SelectBox
                  ignoreP3Margin={true}
                  dataField="basisId"
                  className="productSelectorInSelectBox"
                  value={productInfo.productId}
                  //formData={formData}
                  options={products}
                  placeholder={t('ProductPlaceHolder')}
                  setStateMethod={handleProductSelectChange}
                  isDisabled={false}
                />
              </div>
            </div>
          </>
          )}

          {productInfo && productInfo.productId && (
            <>
              {selectedValues && (
                <div className="row">
                  <div
                    className="col-5"
                    style={{ borderRight: "1px solid lightgray" }}
                  >

                    <div className="row">
                      <div className="col-2">
                        {!fullScreen &&
                          <div className="enterFullScreenButton" onClick={() => {

                            let topNavigationElement = document.getElementById('topNavigationElement');

                            if (topNavigationElement) {
                              topNavigationElement['style']['display'] = 'none';
                            }

                            setFullScreen(true);
                          }}>
                            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="currentColor" className="bi bi-arrows-fullscreen" viewBox="0 0 16 16">
                              <path fill-rule="evenodd" d="M5.828 10.172a.5.5 0 0 0-.707 0l-4.096 4.096V11.5a.5.5 0 0 0-1 0v3.975a.5.5 0 0 0 .5.5H4.5a.5.5 0 0 0 0-1H1.732l4.096-4.096a.5.5 0 0 0 0-.707m4.344 0a.5.5 0 0 1 .707 0l4.096 4.096V11.5a.5.5 0 1 1 1 0v3.975a.5.5 0 0 1-.5.5H11.5a.5.5 0 0 1 0-1h2.768l-4.096-4.096a.5.5 0 0 1 0-.707m0-4.344a.5.5 0 0 0 .707 0l4.096-4.096V4.5a.5.5 0 1 0 1 0V.525a.5.5 0 0 0-.5-.5H11.5a.5.5 0 0 0 0 1h2.768l-4.096 4.096a.5.5 0 0 0 0 .707m-4.344 0a.5.5 0 0 1-.707 0L1.025 1.732V4.5a.5.5 0 0 1-1 0V.525a.5.5 0 0 1 .5-.5H4.5a.5.5 0 0 1 0 1H1.732l4.096 4.096a.5.5 0 0 1 0 .707" />
                            </svg>
                          </div>
                        }

                        {fullScreen &&
                          <div className="exitFullScreenButton" onClick={() => {

                            let topNavigationElement = document.getElementById('topNavigationElement');

                            if (topNavigationElement) {
                              topNavigationElement['style']['display'] = 'block';
                            }

                            setFullScreen(false);
                          }}>
                            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="currentColor" className="bi bi-fullscreen" viewBox="0 0 16 16">
                              <path d="M1.5 1a.5.5 0 0 0-.5.5v4a.5.5 0 0 1-1 0v-4A1.5 1.5 0 0 1 1.5 0h4a.5.5 0 0 1 0 1zM10 .5a.5.5 0 0 1 .5-.5h4A1.5 1.5 0 0 1 16 1.5v4a.5.5 0 0 1-1 0v-4a.5.5 0 0 0-.5-.5h-4a.5.5 0 0 1-.5-.5M.5 10a.5.5 0 0 1 .5.5v4a.5.5 0 0 0 .5.5h4a.5.5 0 0 1 0 1h-4A1.5 1.5 0 0 1 0 14.5v-4a.5.5 0 0 1 .5-.5m15 0a.5.5 0 0 1 .5.5v4a1.5 1.5 0 0 1-1.5 1.5h-4a.5.5 0 0 1 0-1h4a.5.5 0 0 0 .5-.5v-4a.5.5 0 0 1 .5-.5" />
                            </svg>
                          </div>
                        }
                      </div>
                      <div className="col-10">
                        <ProductBreadCrumb hidePath={true} selectionOfProductIsRequired={true} /> </div>
                    </div>
                    <div className="row">
                      <div className="col-12">
                        <SelectBox
                          ignoreP3Margin={true}
                          dataField="basisId"
                          className="productSelectorInSelectBox"
                          value={productInfo.productId}
                          //formData={formData}
                          options={products}
                          placeholder={t('ProductPlaceHolder')}
                          setStateMethod={handleProductSelectChange}
                          isDisabled={false}
                        />
                      </div>

                    </div>
                    {!productInfo ||
                      (!productInfo.productId && (
                        <div className="row" style={{ padding: "20px" }}>
                          <div className={`card`}>
                            <div className="card-body">
                              {t('PleaseSelectProduct')}
                            </div>
                          </div>
                        </div>
                      ))}

                    <div className="row">
                      <div className="col-6" style={{ paddingLeft: '0' }}>
                        <CheckBox
                          id="chkShowOnlySpecialReceipts"
                          label={`${t('ShowOnlySpecialReceipts')}`}
                          paddingLeft={"10px"}
                          handleCheckedChange={(checked) => {

                            const url = new URL(window.location.href);

                            if (checked) {
                              url.searchParams.set("specialreceipts", "true");
                              // Keep filters mutually exclusive.
                              url.searchParams.delete("customreceipts");
                            } else {
                              url.searchParams.delete("specialreceipts");
                            }

                            // hard reload na stejný path (/tonovani) + query
                            window.location.href = url.toString();
                          }}
                          defaultValue={specialReceiptsMode}
                        />
                      </div>
                      <div className="col-6">
                        <CheckBox
                          id="chkShowOnlyCustomReceipts"
                          label={`${t('ShowOnlyCustomReceipts')}`}
                          paddingLeft={"10px"}
                          handleCheckedChange={(checked) => {

                            const url = new URL(window.location.href);

                            if (checked) {
                              url.searchParams.set("customreceipts", "true");
                              // Keep filters mutually exclusive.
                              url.searchParams.delete("specialreceipts");
                            } else {
                              url.searchParams.delete("customreceipts");
                            }

                            // hard reload na stejný path (/tonovani) + query
                            window.location.href = url.toString();
                          }}
                          defaultValue={customReceiptsMode}
                        />
                      </div>
                    </div>
                    {productAlternatives && productAlternatives.length > 1 &&
                      <CheckBox
                        id="chkShowAlternatives"
                        label={`${t('ShowProductAlternatives')} (${productAlternatives.length - 1})`}
                        paddingLeft={"10px"}
                        handleCheckedChange={(checked) => {
                          setAlternativesVisible(checked);
                        }}
                        defaultValue={false}
                      />}


                    {productAlternatives && alternativesVisible && productAlternatives.length > 1 &&
                      <ComboBoxData
                        id="productAlternatives"
                        label={t('Fields.AvailableAlternatives')}
                        dataTextField="name"
                        dataValueField="id"
                        defaultValue={productInfo.productId}
                        handleChange={handleProductAlternativeChange}
                        rows={productAlternatives}
                      />
                    }

                    <ComboBoxData
                      id="sampleCards"
                      key={`cmbSampleCard_${productInfo?.productId ?? 'none'}`}
                      label={t('Fields.SampleCard')}
                      dataTextField="name"
                      dataValueField="id"
                      value={selectedValues?.sampleCard?.id}
                      handleChange={handleSampleCardChange}
                      rows={sampleCards}
                    />

                    <ComboBoxDataEditable
                      id="shade"
                      label={t('Fields.Color')}
                      dataTextField="name"
                      dataValueField="id"
                      value={selectedValues?.color?.id}
                      //colorPropertyForOptions="rgbColor"
                      rows={colors}
                      handleChange={handleColorChange}
                    />

                    <ComboBoxData
                      id="unitSelection"
                      label={t('Fields.Unit')}
                      dataTextField="name"
                      dataValueField="id"
                      defaultValue={defaultUnitId}
                      handleChange={handleUnitChange}
                      rows={[
                        { name: t('Units.Kilograms'), id: "10", code: "Kg" },
                        { name: t('Units.Grams'), id: "11", code: "G" },
                        { name: t('Units.Liters'), id: "21", code: "L" },
                        { name: t('Units.Milliliters'), id: "22", code: "Ml" },
                        { name: t('Units.Ounces'), id: "30", code: "Oz" },
                      ]}
                    />

                    {(selectedValues.unitCode == "Oz" || selectedValues.unitCode == "Ml") && <CheckBox
                      id="unitFormatInYpsilon"
                      label={t('ShowInYFormat')}
                      paddingLeft={"10px"}
                      handleCheckedChange={(checked) => {

                        if (!!userInfo.RememberFieldsValues) {
                          localStorage.setItem("selectedYFormat", checked.toString());
                        }

                        setYFormatIsActive(checked);

                        let newToningData = { ...toningData, yFormatIsActive: checked };
                        setToningData(newToningData);
                      }}
                      defaultValue={defaultYFormat}
                    />}

                    <ComboBoxData
                      id="base"
                      label={t('Fields.Base')}
                      dataTextField="name"
                      dataValueField="id"
                      rows={basis}
                      handleChange={handleBaseChange}
                    />

                    {!customWeight.enabled &&
                      <ComboBoxData
                        id="packages"
                        disabled={customWeight.enabled && customWeight?.value > 0}
                        label={t('Fields.Package')}
                        dataTextField="formattedAmmount"
                        dataValueField="id"
                        value={defaultPackageId!}
                        defaultValue={defaultPackageId!}
                        rows={packages}
                        handleChange={handlePackageChange}
                      />
                    }
                    <div className="row">
                      <div className="col-3"></div>
                      <div className="col-9 customWeightPadding">
                        <CheckBox
                          id="customWeight"
                          label={t('Fields.CustomWeight')}
                          paddingLeft={"10px"}

                          handleCheckedChange={(checked) => {

                            let newCustomWeightState = { ...customWeight };
                            newCustomWeightState.enabled = checked;

                            if (!checked) {
                              newCustomWeightState.value = 0;
                            }
                            else {
                              newCustomWeightState.value = null; // selectedValues?.package?.amount || 1;
                            }

                            setCustomWeight(newCustomWeightState);

                            let newState = { ...selectedValues }
                            recalculateToningData(newState, null, newCustomWeightState);

                          }}
                          defaultValue={customWeight.enabled}
                        />
                      </div>
                    </div>
                    {customWeight.enabled && (
                      <div>
                        <TextBox
                          id="customWeightValue"
                          label={`${t('Fields.CustomWeightValue')} (kg)`}
                          placeholder={t('Fields.CustomWeightValuePlaceHolder')}
                          dataField="value"
                          isNumber={true}
                          min={0}
                          max={1000}
                          step={0.1}

                          defaultValue={customWeight.value}
                          value={customWeight.value}
                          formData={customWeight}
                          setStateMethod={setCustomWeight}
                          handleCheckedChange={(value) => {

                            if (value < 0) {
                              return;
                            }

                            let valueInvariant = value.toString().replace(',', '.');

                            setCustomWeight({ ...customWeight, value: valueInvariant });
                            handleCustomWeightChange(valueInvariant);


                          }}
                          customLeftCols="col-sm-3"
                          customRightCols="col-sm-9"
                        />
                      </div>
                    )}


                    {
                      (!(!selectedValues || receiptForThisColorNotFound || !selectedValues.receipt || invalidProduct)) &&
                      selectedValues &&
                      selectedValues.package &&
                      <>

                        <CheckBox
                          id="weightWithPigmentsIncluded"
                          label={t('Fields.WeightWithPigmentsIncluded')}
                          paddingLeft={"10px"}
                          handleCheckedChange={(checked) => {

                            let newState = { ...selectedValues, weightWithPigmentsIncluded: checked };

                            if (checked) {
                              newState.weightWithHardernerIncluded = false;
                            }

                            recalculateToningData(newState);
                            setSelectedValue(newState);

                          }}
                          defaultValue={selectedValues.weightWithPigmentsIncluded}
                        />
                      </>}

                    {
                      (!(!selectedValues || receiptForThisColorNotFound || !selectedValues.receipt || invalidProduct)) &&
                      selectedValues &&
                      selectedValues.package &&
                      selectedValues.package.hardeners &&
                      selectedValues.package.hardeners.length > 0 &&

                      <>

                        <CheckBox
                          id="weightWithHardernerIncluded"
                          label={t('Fields.WeightWithHardernerIncluded')}
                          paddingLeft={"10px"}
                          handleCheckedChange={(checked) => {

                            let newState = { ...selectedValues, weightWithHardernerIncluded: checked };

                            if (checked) {
                              newState.weightWithPigmentsIncluded = false;
                            }

                            recalculateToningData(newState);
                            setSelectedValue(newState);

                          }}
                          defaultValue={selectedValues.weightWithHardernerIncluded}
                        />
                      </>}


                    {!invalidProduct &&
                      <ComboBoxData
                        id="discounts"
                        label={t('Fields.Discounts')}
                        dataTextField="name"
                        dataValueField="id"
                        defaultValue={
                          primaryDiscount && primaryDiscount.length > 0
                            ? primaryDiscount[0]
                            : null
                        }
                        rows={discounts}
                        handleChange={handleDiscountChange}
                      />}

                    {selectedValues?.color && !receiptForThisColorNotFound && !invalidProduct && (
                      <>
                        <div className="row">
                          <div className="col-sm-3">
                            <label className="col-form-label zvlabel">
                              <div>{t('Fields.ColorPreview')}</div>
                              <div style={{ display: "none" }}>{selectedValues.color.colorName}</div>
                              {toningData?.isCustomReceipt && <span style={{ color: 'lightgray' }}>{t('CustomReceipt')}</span>}
                            </label>
                          </div>
                          <div className="col-sm-9">
                            {selectedValues.color.isPicture && <div style={{ textAlign: 'center' }}>
                              <Picture width={80} height={80} fileName={selectedValues.color.pictureName} />
                            </div>}
                            {!selectedValues.color.isPicture &&
                              <div
                                style={{ padding: "0px" }}
                                className="card text-center colorPreviewCardContainer"
                              >
                                <div
                                  className="card-body colorPreviewContainer"
                                  style={{
                                    backgroundColor: selectedValues.color.rgbColor,
                                  }}
                                ></div>
                              </div>}
                          </div>
                        </div>

                      </>
                    )}
                  </div>
                  <div className="col-7">
                    {receiptForThisColorNotFound && <div>

                    </div>}

                    {!yFormatIsActive && !receiptForThisColorNotFound && !invalidProduct &&
                      <DataGrid
                        key={`${i18n.language}_tonovaniGrid_noYformat`}
                        additionalClassName="tonovaniGrid"
                        loading={false}
                        rows={
                          toningData &&
                            toningData.pigments &&
                            toningData.pigments.length > 0
                            ? toningData.pigments
                            : []
                        }
                        showDeleteButton={false}
                        showEditButton={false}
                        cols={[
                          { name: pigmentsGridColumnNames.PigmentName, dataField: "name" },
                          { name: pigmentsGridColumnNames.AmountInUnit, dataField: "formattedAmountInUnit" },
                          { name: pigmentsGridColumnNames.Price, dataField: "formattedPrice" },
                        ]}
                      />
                    }

                    {yFormatIsActive && !receiptForThisColorNotFound && !invalidProduct &&
                      <DataGrid
                        key={`${i18n.language}_tonovaniGrid_${yFormatIsActive}`}
                        additionalClassName="tonovaniGrid"
                        loading={false}
                        rows={
                          toningData &&
                            toningData.pigments &&
                            toningData.pigments.length > 0
                            ? toningData.pigments
                            : []
                        }
                        showDeleteButton={false}
                        showEditButton={false}
                        cols={[
                          { name: pigmentsGridColumnNames.PigmentName, dataField: "name" },
                          { name: pigmentsGridColumnNames.YFormat, dataField: "formattedAmountInYFormat" },
                          { name: pigmentsGridColumnNames.AmountInUnit, dataField: "formattedAmountInUnit" },
                          { name: pigmentsGridColumnNames.Price, dataField: "formattedPrice" },
                        ]}
                      />
                    }

                    {toningData.hardeners && !receiptForThisColorNotFound && !invalidProduct &&
                      <DataGrid
                        key={`${i18n.language}_hardenerGrid`}
                        additionalClassName="hardenerGrid"
                        loading={false}
                        rows={toningData.hardeners}

                        showDeleteButton={false}
                        showEditButton={false}
                        cols={[
                          { name: pigmentsGridColumnNames.HardenerName, dataField: "name" },
                          { name: pigmentsGridColumnNames.AmountInUnit, dataField: "formattedSize" },
                          { name: pigmentsGridColumnNames.Price, dataField: "formattedPrice" },
                        ]}
                      />
                    }

                    {selectedValues && selectedValues.receipt && selectedValues.receipt.pigmentsRatio != null &&
                      selectedValues.receipt.pigmentsRatio > 0 && <div style={{ textAlign: 'center' }}>
                        <div>{t('Fields.PigmentsRatio')}</div>
                        <a
                          className={`btn btn-sm btn-primary ${selectedValues.selectedPigmentsRatio == selectedValues.receipt.pigmentsRatio ? 'selectedPigmentsRatio' : ''} `}
                          onClick={() => {
                            let currentState = selectedValues;
                            currentState.selectedPigmentsRatio = selectedValues.receipt.pigmentsRatio;
                            recalculateToningData(selectedValues);
                            setSelectedValue(currentState);
                          }}
                          style={{
                            backgroundColor: " #55acee;",
                          }}
                          role="button"
                        >
                          {selectedValues.receipt.pigmentsRatio}
                        </a>
                        <a
                          className={`btn btn-sm btn-primary ${selectedValues.selectedPigmentsRatio == (selectedValues.receipt.pigmentsRatio * 2) ? 'selectedPigmentsRatio' : ''} `}
                          onClick={() => {
                            let currentState = selectedValues;
                            currentState.selectedPigmentsRatio = selectedValues.receipt.pigmentsRatio * 2;
                            recalculateToningData(selectedValues);
                            setSelectedValue(currentState);
                          }}
                          style={{
                            backgroundColor: " #55acee;",
                          }}
                          role="button"
                        >
                          {selectedValues.receipt.pigmentsRatio * 2}
                        </a>
                        <a
                          className={`btn btn-sm btn-primary ${selectedValues.selectedPigmentsRatio == (selectedValues.receipt.pigmentsRatio * 3) ? 'selectedPigmentsRatio' : ''} `}
                          onClick={() => {
                            let currentState = selectedValues;
                            currentState.selectedPigmentsRatio = selectedValues.receipt.pigmentsRatio * 3;
                            recalculateToningData(selectedValues);
                            setSelectedValue(currentState);
                          }}
                          style={{
                            backgroundColor: " #55acee;",
                          }}
                          role="button"
                        >
                          {selectedValues.receipt.pigmentsRatio * 3}
                        </a>
                      </div>}
                  </div>
                </div>
              )}

              {selectedValues && selectedValues.receipt && !receiptForThisColorNotFound && !invalidProduct && (
                <div className="row">
                  <div className="col-5">
                    <div style={{ display: "flex" }} className="tonovaniHeader">
                      <i
                        style={{ marginRight: "10px" }}
                        className="fas fa-balance-scale"
                      ></i>
                      <span>{t('Summary.Weight')}</span>
                    </div>
                    <div className="row">
                      <div className="col-6">{t('Summary.WeightBasis')}</div>
                      <div className="col-6 tonovaniWeightValue">
                        {toningData.formattedBaseVolume}
                      </div>
                    </div>
                    <div className="row">
                      <div className="col-6">{t('Summary.WeightPigments')}</div>
                      <div className="col-6 tonovaniWeightValue">
                        {toningData.formattedPigmentsVolume}
                      </div>
                    </div>

                    {toningData.hardeners && toningData.hardeners.length > 0 &&
                      <div className="row">
                        <div className="col-6">{t('Summary.WeightHardener')}</div>
                        <div className="col-6 tonovaniWeightValue">
                          {toningData.formattedHardenersVolume}
                        </div>
                      </div>
                    }
                    <div
                      className="row"
                      style={{ borderTop: "1px solid gray" }}
                    >
                      <div className="col-6">{t('Summary.WeightTotal')}</div>
                      <div
                        className="col-6 tonovaniWeightValue"
                        style={{ fontWeight: "bold" }}
                      >
                        {toningData.formattedVolume}
                      </div>
                    </div>

                    {selectedValues.receipt?.noteExternal && (
                      <>
                        <div
                          className="row"
                          style={{
                            borderTop: "1px solid gray",
                            marginTop: "20px",
                          }}
                        >
                          <div className="col-12">{t('Summary.Note')}</div>

                        </div>

                        <div className="row">
                          <div
                            className="col-12"
                            style={{ fontWeight: "bold" }}
                          >
                            {selectedValues.receipt.noteExternal}
                          </div></div></>
                    )}
                  </div>
                  <div className="col-7">
                    <div style={{ display: "flex" }} className="tonovaniHeader">
                      <i
                        style={{ marginRight: "10px" }}
                        className="fas fa-hand-holding-usd"
                      ></i>
                      <span>{t('Summary.Price')}</span>
                    </div>
                    <div className="row">
                      <div className="col-6">{t('Summary.PriceBasis')}</div>
                      <div className="col-6 priceValue">
                        {toningData.formattedBasePrice}
                      </div>
                    </div>

                    <div className="row">
                      <div className="col-6">{t('Summary.PricePigments')}</div>
                      <div className="col-6 priceValue">
                        {toningData.formattedPigmentsPrice}
                      </div>
                    </div>

                    {
                      toningData.hardeners && toningData.hardeners.length > 0 &&
                      <div className="row">
                        <div className="col-6">{t('Summary.PriceHardener')}</div>
                        <div className="col-6 priceValue">
                          {toningData.formattedHardenersPrice}
                        </div>
                      </div>
                    }

                    <div
                      className="row"
                      style={{ borderTop: "1px solid gray" }}
                    >
                      <div className="col-6">{t('Summary.PricePackage')}</div>
                      <div className="col-6 priceValue">
                        {toningData.formattedPackagePriceWithoutVAT}
                      </div>
                    </div>

                    <div className="row">
                      <div className="col-6">{t('Summary.PricePackageVat')}</div>
                      <div className="col-6 priceValue">
                        {toningData.formattedPackagePriceWithVAT}
                      </div>
                    </div>

                    <div className="row">
                      <div className="col-6" style={{ alignSelf: "center" }}>
                        {t('Summary.PricePackagesCount')}
                      </div>

                      <div
                        className="col-3 priceValue"
                        style={{ justifyContent: "right" }}
                      >
                        <div className="row">
                          <div className="col-6">
                            <a
                              className="btn btn-sm btn-primary"
                              onClick={() => {
                                if (numberOfPackages > 1) {
                                  let packages = numberOfPackages - 1;
                                  setNumberOfPackages(packages);
                                  recalculateToningData(
                                    selectedValues,
                                    packages
                                  );
                                }
                              }}
                              style={{
                                backgroundColor: " #55acee;",
                              }}
                              role="button"
                            >
                              <i className="fas fa-minus"></i>
                            </a>
                          </div>
                          <div className="col-6">
                            <a
                              className="btn btn-sm btn-primary"
                              onClick={() => {
                                let packages = numberOfPackages + 1;
                                setNumberOfPackages(packages);
                                recalculateToningData(selectedValues, packages);
                              }}
                              style={{
                                backgroundColor: " #55acee;",
                              }}
                              role="button"
                            >
                              <i className="fas fa-plus"></i>
                            </a>
                          </div>

                        </div>
                      </div>

                      <div
                        className="col-3 priceValue"
                        style={{ justifyContent: "right", alignSelf: "center" }}
                      >
                        <label style={{ fontWeight: "bold" }}>
                          {numberOfPackages}
                        </label>
                      </div>
                    </div>

                    <div
                      className="row"
                      style={{
                        borderTop: "1px solid gray",
                        fontWeight: "bold",
                        color: "blue",
                      }}
                    >
                      <div className="col-6">{t('Summary.PriceTotal')}</div>
                      <div className="col-6 priceValue">
                        {toningData.formattedTotalPriceWithoutVAT}
                      </div>
                    </div>

                    <div
                      className="row"
                      style={{
                        fontWeight: "bold",
                        color: "blue",
                        borderBottom: "1px solid gray",
                      }}
                    >
                      <div className="col-6">{t('Summary.PriceTotalVat')}</div>
                      <div className="col-6 priceValue">
                        {toningData.formattedTotalPriceWithVAT}
                      </div>
                    </div>

                    <ToningPrinterOptions
                      toningData={{ ...toningData }}
                      handlePrint={printCurrentToning} />
                  </div>
                </div>
              )}

              {(!selectedValues || receiptForThisColorNotFound || !selectedValues.receipt || invalidProduct) && (
                <div className="row" style={{ padding: "20px" }}>
                  <div className={`card`}>
                    <div className="card-body"
                      style={{
                        fontWeight: 'bold',
                        fontSize: '1.5rem',
                        color: 'red',
                        textAlign: 'center'
                      }}>
                      {t('NoReceiptForThisCombination')}
                    </div>
                  </div>
                </div>
              )}
            </>
          )}
        </>
      </PageContent>
    </>
  );
};

export default TonovaniPage;
